import { useState } from 'react';

import { extractStreetNumber } from '@utilityprofit/address-helpers';
import { SR_API_ACTION_ADDRESS_REGISTER_PARTNER_CONFIRMED } from 'api/utilityActionTypes';
import requestUtilityApi from 'client/actions/requestUtilityApi';
import { stateOptionItems } from 'common/util/stateCodes';

const getManualAddressFields = () => [
  {
    id: 'streetLine1',
    title: 'Address Line 1 *',
    type: 'short_text',
    properties: {
      layout: 'full',
      placeholder: 'Street Address',
    },
    validations: ['required', 'streetName'],
  },
  {
    id: 'unitNumber',
    title: 'Address Line 2',
    type: 'short_text',
    properties: {
      layout: 'full',
      placeholder: 'Apartment, suite, floor',
    },
    validations: [],
  },
  {
    id: 'city',
    title: 'City *',
    type: 'short_text',
    properties: {
      layout: 'third',
      placeholder: 'City',
    },
    validations: ['required', 'city'],
  },
  {
    id: 'stateCode',
    title: 'State *',
    type: 'select',
    properties: {
      layout: 'third',
      choices: stateOptionItems.map(({ value }) => ({ label: value, value })),
      placeholder: 'State',
      optionsContainerMaxHeight: '180px',
      transformToUppercase: true,
    },
    validations: ['required', 'stateCode'],
  },
  {
    id: 'zip',
    title: 'ZIP *',
    type: 'short_text',
    properties: {
      layout: 'third',
      placeholder: 'ZIP',
    },
    validations: ['required', 'zipCode'],
  },
];

const getManualAddressFieldsValid = (formValues) => {
  return getManualAddressFields()
    .filter((field) => field.validations?.includes('required'))
    .every((field) => Boolean(formValues[field.id]));
};

const getManualFieldGroups = ({ utilityPartnerField } = {}) => {
  const [addressLine1, addressLine2, city, state, zip] = getManualAddressFields();
  const fieldGroups = [];

  if (utilityPartnerField) {
    fieldGroups.push({ fields: [utilityPartnerField], layoutClassName: 'defaultRow' });
  }

  fieldGroups.push(
    { fields: [addressLine1], layoutClassName: 'defaultRow' },
    { fields: [addressLine2], layoutClassName: 'defaultRow' },
    { fields: [city, state, zip], layoutClassName: 'thirdsRow' },
  );

  return { fieldGroups };
};

const useAddressRegisterManualEntry = ({
  dispatch,
  afterSubmit,
  utilityPartnerId,
  onRegisterSuccess,
}) => {
  const [submitErrorMessage, setSubmitErrorMessage] = useState(null);

  const onSubmit = (formValues, formikBag) => {
    const {
      utilityPartnerId: formUtilityPartnerId,
      streetLine1,
      unitNumber,
      city,
      stateCode,
      zip,
    } = formValues;
    const streetNumber = extractStreetNumber(streetLine1);
    const streetName = streetLine1.replace(streetNumber || '', '').trim() || streetLine1;
    setSubmitErrorMessage(null);

    dispatch(requestUtilityApi, {
      action: SR_API_ACTION_ADDRESS_REGISTER_PARTNER_CONFIRMED,
      payload: {
        partnerId: parseInt(formUtilityPartnerId, 10),
        address: {
          streetNumber: streetNumber || null,
          streetName,
          unitNumber: unitNumber || null,
          city,
          stateCode,
          zip,
        },
      },
      onSuccess(address) {
        formikBag.resetForm();

        if (afterSubmit) {
          afterSubmit(address);
        }

        if (onRegisterSuccess) {
          onRegisterSuccess(address);
        } else if (!utilityPartnerId) {
          global.alert('Address registered successfully.');
        }
      },
      onError(error) {
        setSubmitErrorMessage(error?.message);
      },
    });
  };

  return {
    onSubmit,
    submitErrorMessage,
  };
};

const getManualEntryHandler = ({
  setIsManualAddressEntry,
  formikBag,
  initialFormFields,
  utilityPartnerId,
  createInitialValues,
}) => {
  return () => {
    setIsManualAddressEntry(true);
    formikBag.resetForm({
      values: createInitialValues({
        formFields: initialFormFields,
        defaultValues: {
          utilityPartnerId,
        },
      }),
    });
  };
};

export {
  getManualAddressFields,
  getManualAddressFieldsValid,
  getManualFieldGroups,
  getManualEntryHandler,
  useAddressRegisterManualEntry,
};
